import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { CATALOG } from './estimateData'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

const CACHE_KEY = 'catalog_items'

// Fast lookup for original seeded values (for reset + isModified detection)
export const CATALOG_MAP = Object.fromEntries(CATALOG.map(i => [i.id, i]))

/**
 * useCatalogItems — fetches catalog_items from Supabase.
 * Falls back to the hardcoded CATALOG on error.
 *
 * @param {object|null} session  — Supabase session (used only for updated_by)
 * @returns {{ items, loading, error, upsertItem, removeItem, reload }}
 */
export function useCatalogItems(session) {
    const [items,   setItems]   = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)
    const userId = session?.user?.id || null
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)

        const cached = await loadReferenceCache(CACHE_KEY, null).catch(() => null)
        if (cached) {
            setItems(cached.data)
            if (!silent) setLoading(false)
        }

        const { data, error: err } = await supabase
            .from('catalog_items')
            .select('*')
            .order('cat',  { ascending: true })
            .order('name', { ascending: true })

        if (id !== loadIdRef.current) return

        if (err) {
            console.error('[catalogItems] fetch error:', err.message)
            setError(err.message)
            if (!cached) setItems(CATALOG)
        } else {
            setItems(data || [])
            await saveReferenceCache(CACHE_KEY, data || [], null).catch(() => {})
        }
        if (!silent) setLoading(false)
    }, [])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    /** Upsert a full item row. Updates local state optimistically. */
    const upsertItem = useCallback(async (item) => {
        // Only send columns that exist in the catalog_items table
        const payload = {
            id:         item.id,
            cat:        item.cat,
            name:       item.name,
            unit:       item.unit,
            price:      item.price,
            cost:       item.cost,
            inc:        item.inc     ?? [],
            exc:        item.exc     ?? [],
            inc_ids:    item.inc_ids ?? [],
            exc_ids:    item.exc_ids ?? [],
            subcat:     item.subcat  ?? null,
            is_custom:  item.is_custom ?? false,
            updated_by: userId,
            updated_at: new Date().toISOString(),
        }
        // Optimistic update
        setItems(prev => {
            const idx = prev.findIndex(i => i.id === payload.id)
            if (idx === -1) return [...prev, payload]
            const next = [...prev]
            next[idx] = payload
            return next
        })
        const { data, error: err } = await supabase
            .from('catalog_items')
            .upsert(payload, { onConflict: 'id' })
            .select()
            .single()
        if (err) {
            console.error('[catalogItems] upsert error:', err.message)
            throw new Error(err.message)
        }
        // Sync with server response
        setItems(prev => {
            const idx = prev.findIndex(i => i.id === data.id)
            if (idx === -1) return [...prev, data]
            const next = [...prev]
            next[idx] = data
            return next
        })
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
        return data
    }, [userId])

    /** Delete a catalog item (only call for is_custom = true rows). */
    const removeItem = useCallback(async (id) => {
        setItems(prev => prev.filter(i => i.id !== id))
        const { error: err } = await supabase
            .from('catalog_items')
            .delete()
            .eq('id', id)
        if (err) {
            console.error('[catalogItems] delete error:', err.message)
            throw new Error(err.message)
        }
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
    }, [])

    /** Bulk-rename cat field on all items matching oldName. Optimistic + server. */
    const renameCatItems = useCallback(async (oldName, newName) => {
        const trimmed = newName.trim().toUpperCase()
        if (!trimmed || trimmed === oldName) return
        setItems(prev => prev.map(i => i.cat === oldName ? { ...i, cat: trimmed } : i))
        const { error: err } = await supabase
            .from('catalog_items')
            .update({ cat: trimmed })
            .eq('cat', oldName)
        if (err) {
            console.error('[catalogItems] renameCat error:', err.message)
            setItems(prev => prev.map(i => i.cat === trimmed ? { ...i, cat: oldName } : i))
            throw new Error(err.message)
        }
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
    }, [])

    return { items, loading, error, upsertItem, removeItem, renameCatItems, reload: load, silentReload }
}

/**
 * Returns true if the DB row differs from the original seeded catalog value.
 * Custom items are never considered "modified" by this check.
 */
export function isItemModified(item) {
    if (item.is_custom) return false
    const orig = CATALOG_MAP[item.id]
    if (!orig) return false
    return (
        Number(item.price) !== Number(orig.price) ||
        Number(item.cost)  !== Number(orig.cost)  ||
        (item.subcat || null) !== (orig.subcat || null) ||
        JSON.stringify(item.inc) !== JSON.stringify(orig.inc ?? []) ||
        JSON.stringify(item.exc) !== JSON.stringify(orig.exc ?? [])
    )
}
